/**
 * Política de Privacidade — Sistema de Arenas Meiuca (B2B).
 *
 *  RASCUNHO PRELIMINAR — REVISÃO JURÍDICA OBRIGATÓRIA antes da publicação oficial.
 *
 * Endpoint público esperado: https://meiucaapp.com.br/arenas/privacidade
 *
 * Cobre o tratamento de dados pessoais de operadores e Clientes Finais
 * realizados pela Meiuca no contexto do Sistema de Arenas, incluindo o
 * compartilhamento com o ASAAS GESTÃO FINANCEIRA INSTITUIÇÃO DE PAGAMENTOS S.A.
 * para cumprimento das exigências regulatórias do BACEN (KYC, PLD/FT,
 * Resolução Conjunta nº 16/2025).
 *
 * Migrado de `meiuca-site` (Next.js) para `meiuca-app-web-react` (React Router).
 */

import { Body, H2, H3 } from '@meiuca/ui-components'
import { LegalPageLayout } from '../components/LegalPageLayout'

const arenaRelatedLinks = [
  { href: '/arenas/termos', label: 'Termos do Sistema de Arenas' },
  { href: '/arenas/privacidade', label: 'Privacidade do Sistema de Arenas' },
  { href: '/termos', label: 'Termos do app Meiuca (consumidor)' }
]

export function ArenasPrivacidadePage() {
  return (
    <LegalPageLayout
      title="Política de Privacidade — Sistema de Arenas"
      documentTitle="Política de Privacidade — Sistema de Arenas — Meiuca"
      lastUpdated="20 de maio de 2026"
      relatedLinks={arenaRelatedLinks}
    >
      <Body>
        Esta Política de Privacidade descreve como a Meiuca trata os dados pessoais
        coletados no <strong>Sistema de Arenas</strong>, em conformidade com a Lei Geral
        de Proteção de Dados (Lei nº 13.709/2018 — LGPD) e com a Resolução Conjunta
        nº 16/2025 do Banco Central do Brasil. Ao aderir ao Sistema de Arenas, a Arena
        concorda com as práticas descritas aqui.
      </Body>

      <section>
        <H2 className="text-xl md:text-2xl mt-8 mb-3">1. Quem somos</H2>
        <Body>
          O Sistema de Arenas é operado por <strong>Rafael Balmant Letro — ME</strong>,
          inscrita no CNPJ sob nº <strong>27.422.545/0001-42</strong> e Inscrição
          Municipal nº <strong>00098036</strong>, com sede na{' '}
          <strong>Rua São Marcos, 370 — Bairro São Vito — Americana/SP — CEP 13472-200</strong>.
          Para fins da LGPD, somos o <strong>controlador</strong> dos dados pessoais
          de operadores da Arena e <strong>operador</strong> dos dados de Clientes
          Finais que a Arena nos confiar para fins de execução do serviço, conforme
          determinado nestes documentos.
        </Body>
      </section>

      <section>
        <H2 className="text-xl md:text-2xl mt-10 mb-3">2. Quais dados coletamos</H2>

        <H3 className="text-lg mt-4 mb-2">2.1 Dados dos operadores da Arena</H3>
        <ul className="list-disc pl-6 space-y-2 text-text-secondary">
          <li>Nome completo, e-mail, telefone, senha (hash) e fotografia (opcional).</li>
          <li>Cargo/função na Arena e nível de acesso.</li>
          <li>Logs técnicos de acesso (data/hora, IP, navegador, ações realizadas).</li>
        </ul>

        <H3 className="text-lg mt-6 mb-2">2.2 Dados da Arena e do titular</H3>
        <ul className="list-disc pl-6 space-y-2 text-text-secondary">
          <li>Razão social ou nome completo (PF), CPF ou CNPJ, data de nascimento (PF).</li>
          <li>E-mail e telefone de cobrança.</li>
          <li>Endereço completo (rua, número, complemento, bairro, CEP, cidade, estado).</li>
          <li>Renda mensal (PF) ou faturamento mensal (PJ).</li>
          <li>Chave Pix para recebimento de saques (tipo e identificador, armazenado mascarado).</li>
        </ul>

        <H3 className="text-lg mt-6 mb-2">2.3 Dados gerados pelo uso</H3>
        <ul className="list-disc pl-6 space-y-2 text-text-secondary">
          <li>Quadras, planos de preço, agendas, bloqueios, day use e configurações de meios de pagamento.</li>
          <li>Reservas, cobranças, refunds, saldos, ledger e extratos.</li>
          <li>Comunicações via suporte e notificações enviadas.</li>
        </ul>

        <H3 className="text-lg mt-6 mb-2">2.4 Dados de Clientes Finais (sob controle da Arena)</H3>
        <Body>
          A Arena pode cadastrar Clientes Finais (jogadores, alunos, mensalistas) com nome,
          telefone, e-mail e, opcionalmente, CPF. A Meiuca atua como <strong>operadora</strong>{' '}
          desses dados, processando-os exclusivamente conforme orientação da Arena
          (controladora) e estes Termos.
        </Body>
      </section>

      <section>
        <H2 className="text-xl md:text-2xl mt-10 mb-3">
          3. Compartilhamento com o ASAAS
        </H2>
        <Body>
          Para que a Arena possa receber pagamentos online no marketplace Meiuca, é
          necessário abrir uma <strong>Subconta no ASAAS</strong>. Para tanto,
          compartilhamos com o ASAAS:
        </Body>
        <ul className="list-disc pl-6 space-y-2 text-text-secondary mt-3">
          <li>Razão social ou nome completo, CPF/CNPJ, e-mail e telefone de cobrança;</li>
          <li>Data de nascimento (PF);</li>
          <li>Renda/faturamento mensal;</li>
          <li>Endereço completo;</li>
          <li>Chave Pix e dados de saque.</li>
        </ul>
        <Body className="mt-3">
          O ASAAS, na qualidade de instituição de pagamento autorizada pelo BACEN, atua
          como <strong>controlador autônomo</strong> desses dados para cumprimento das
          obrigações de KYC, PLD/FT, prevenção a fraude, retenções legais e demais
          exigências regulatórias. O tratamento realizado pelo ASAAS é regido pela{' '}
          <a
            href="https://www.asaas.com/politica-de-privacidade"
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand-primary hover:underline"
          >
            Política de Privacidade do ASAAS
          </a>
          .
        </Body>
        <Body className="mt-3">
          Adicionalmente, em cada cobrança Pix emitida pelo marketplace, transmitimos ao
          ASAAS os dados mínimos do Cliente Final pagador (nome, telefone, CPF) para
          atender às exigências regulatórias do BACEN para o processamento.
        </Body>
      </section>

      <section>
        <H2 className="text-xl md:text-2xl mt-10 mb-3">4. Para que usamos esses dados</H2>
        <ul className="list-disc pl-6 space-y-2 text-text-secondary">
          <li>Autenticar operadores e manter sessões seguras.</li>
          <li>Operar as funcionalidades do Sistema de Arenas contratadas.</li>
          <li>Processar reservas, cobranças, refunds e saques via ASAAS.</li>
          <li>Comunicar eventos relevantes (aprovação de Subconta, falhas, webhooks).</li>
          <li>Atender obrigações legais, fiscais, regulatórias e contratuais.</li>
          <li>Investigar fraudes, abuso e proteger a integridade da plataforma.</li>
          <li>Melhorar o produto a partir de métricas agregadas e despersonalizadas.</li>
        </ul>
      </section>

      <section>
        <H2 className="text-xl md:text-2xl mt-10 mb-3">5. Bases legais (LGPD)</H2>
        <ul className="list-disc pl-6 space-y-2 text-text-secondary">
          <li>
            <strong>Execução de contrato</strong> (art. 7º, V) — para operar o Sistema
            de Arenas e processar pagamentos.
          </li>
          <li>
            <strong>Cumprimento de obrigação legal/regulatória</strong> (art. 7º, II) —
            para atender ao BACEN, à Receita Federal e à LGPD.
          </li>
          <li>
            <strong>Legítimo interesse</strong> (art. 7º, IX) — para prevenção a fraude,
            segurança da plataforma e melhoria do produto.
          </li>
          <li>
            <strong>Consentimento</strong> (art. 7º, I) — em hipóteses específicas
            indicadas no momento da coleta.
          </li>
        </ul>
      </section>

      <section>
        <H2 className="text-xl md:text-2xl mt-10 mb-3">6. Com quem mais compartilhamos</H2>
        <ul className="list-disc pl-6 space-y-2 text-text-secondary">
          <li>
            <strong>Provedores de infraestrutura</strong> (hospedagem em nuvem, banco de
            dados, envio de e-mail e SMS), sob contrato de operação.
          </li>
          <li>
            <strong>Autoridades públicas</strong>, quando exigido por lei ou ordem judicial.
          </li>
          <li>
            <strong>Parceiros estritamente necessários</strong> para a execução do serviço
            contratado pela Arena, sempre com base legal apropriada.
          </li>
        </ul>
        <Body className="mt-3">
          Não vendemos seus dados nem os usamos para perfilamento publicitário próprio
          ou de terceiros.
        </Body>
      </section>

      <section>
        <H2 className="text-xl md:text-2xl mt-10 mb-3">7. Retenção</H2>
        <Body>
          Dados cadastrais da Arena são mantidos enquanto a adesão estiver ativa. Após
          a rescisão, dados são mantidos pelo período mínimo necessário para o cumprimento
          de obrigações fiscais, contratuais e regulatórias (em regra, 5 anos para
          registros financeiros). Dados sensíveis para KYC e prevenção à lavagem ficam
          sob a custódia do ASAAS pelos prazos exigidos pelo BACEN.
        </Body>
      </section>

      <section>
        <H2 className="text-xl md:text-2xl mt-10 mb-3">8. Direitos dos titulares</H2>
        <Body>A LGPD (art. 18) garante aos titulares:</Body>
        <ul className="list-disc pl-6 space-y-2 text-text-secondary mt-2">
          <li>Confirmar a existência e acessar os dados.</li>
          <li>Corrigir dados incompletos, inexatos ou desatualizados.</li>
          <li>Solicitar anonimização, bloqueio ou eliminação de dados desnecessários.</li>
          <li>Pedir portabilidade para outro fornecedor.</li>
          <li>Revogar consentimento, quando aplicável.</li>
          <li>Apresentar reclamação à ANPD.</li>
        </ul>
        <Body className="mt-4">
          Solicitações de Clientes Finais cadastrados pela Arena devem ser direcionadas
          primeiramente à própria Arena, que é a controladora desses dados. A Meiuca
          apoia o atendimento na qualidade de operadora.
        </Body>
      </section>

      <section>
        <H2 className="text-xl md:text-2xl mt-10 mb-3">9. Segurança</H2>
        <Body>
          A comunicação com o Sistema de Arenas é feita por HTTPS. Senhas são armazenadas
          com hash. Tokens de autenticação são guardados em armazenamento seguro do
          navegador. Adotamos controles de acesso por função (RBAC) e monitoramento de
          eventos de segurança. Em caso de incidente que afete os dados, comunicaremos
          a Arena, os titulares e a ANPD nos prazos legais.
        </Body>
      </section>

      <section>
        <H2 className="text-xl md:text-2xl mt-10 mb-3">10. Alterações nesta política</H2>
        <Body>
          Esta política pode ser atualizada para refletir mudanças no produto, na
          regulamentação ou na infraestrutura. Mudanças relevantes serão comunicadas
          à Arena por e-mail ou por aviso no Sistema de Arenas.
        </Body>
      </section>

      <section>
        <H2 className="text-xl md:text-2xl mt-10 mb-3">11. Contato</H2>
        <Body>
          Encarregado pelo tratamento de dados (DPO):{' '}
          <a
            href="mailto:privacidade@meiucaapp.com.br"
            className="text-brand-primary hover:underline"
          >
            privacidade@meiucaapp.com.br
          </a>
          . Para assuntos sobre o tratamento realizado pelo ASAAS, contate diretamente
          o DPO da instituição: <a
            href="mailto:dpo@asaas.com.br"
            className="text-brand-primary hover:underline"
          >
            dpo@asaas.com.br
          </a>
          .
        </Body>
      </section>
    </LegalPageLayout>
  )
}
